import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import notificationService from '../../../services/notificationService';

const NotificationsTab = ({ language }) => {
  const [notifications, setNotifications] = useState([]);
  const [preferences, setPreferences] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeView, setActiveView] = useState('all');
  const [showPreferences, setShowPreferences] = useState(false);
  const [browserPermission, setBrowserPermission] = useState(false);

  const content = {
    en: {
      title: 'Notifications',
      all: 'All',
      unread: 'Unread',
      preferences: 'Preferences',
      stats: 'Statistics',
      total: 'Total',
      unreadCount: 'Unread',
      read: 'Read',
      byType: 'By Type',
      byChannel: 'By Channel',
      push: 'Push',
      email: 'Email',
      both: 'Both',
      noNotifications: 'No notifications yet',
      loading: 'Loading notifications...',
      markAllRead: 'Mark All as Read',
      markRead: 'Mark as Read',
      delete: 'Delete',
      enableBrowserNotifications: 'Enable Browser Notifications',
      browserNotificationsEnabled: 'Browser notifications enabled',
      browserNotificationsDisabled: 'Browser notifications disabled',
      savePreferences: 'Save Preferences',
      cancel: 'Cancel',
      notificationTypes: {
        order_confirmed: 'Order Confirmed',
        payment_pending: 'Payment Pending',
        payment_verified: 'Payment Verified',
        payment_failed: 'Payment Failed',
        shipment_scheduled: 'Shipment Scheduled',
        in_transit: 'In Transit',
        delivered: 'Delivered',
        dispute_opened: 'Dispute Opened',
        dispute_resolved: 'Dispute Resolved',
        status_changed: 'Status Changed',
        note_added: 'Note Added'
      },
      preferenceLabels: {
        orderConfirmedPush: 'Order Confirmed (Push)',
        orderConfirmedEmail: 'Order Confirmed (Email)',
        paymentPendingPush: 'Payment Pending (Push)',
        paymentPendingEmail: 'Payment Pending (Email)',
        paymentVerifiedPush: 'Payment Verified (Push)',
        paymentVerifiedEmail: 'Payment Verified (Email)',
        paymentFailedPush: 'Payment Failed (Push)',
        paymentFailedEmail: 'Payment Failed (Email)',
        shipmentScheduledPush: 'Shipment Scheduled (Push)',
        shipmentScheduledEmail: 'Shipment Scheduled (Email)',
        inTransitPush: 'In Transit (Push)',
        inTransitEmail: 'In Transit (Email)',
        deliveredPush: 'Delivered (Push)',
        deliveredEmail: 'Delivered (Email)',
        disputeOpenedPush: 'Dispute Opened (Push)',
        disputeOpenedEmail: 'Dispute Opened (Email)',
        disputeResolvedPush: 'Dispute Resolved (Push)',
        disputeResolvedEmail: 'Dispute Resolved (Email)',
        statusChangedPush: 'Status Changed (Push)',
        statusChangedEmail: 'Status Changed (Email)',
        noteAddedPush: 'Note Added (Push)',
        noteAddedEmail: 'Note Added (Email)'
      }
    },
    zh: {
      title: '通知',
      all: '全部',
      unread: '未读',
      preferences: '偏好设置',
      stats: '统计',
      total: '总计',
      unreadCount: '未读',
      read: '已读',
      byType: '按类型',
      byChannel: '按渠道',
      push: '推送',
      email: '邮件',
      both: '两者',
      noNotifications: '尚无通知',
      loading: '加载通知中...',
      markAllRead: '全部标记为已读',
      markRead: '标记为已读',
      delete: '删除',
      enableBrowserNotifications: '启用浏览器通知',
      browserNotificationsEnabled: '浏览器通知已启用',
      browserNotificationsDisabled: '浏览器通知已禁用',
      savePreferences: '保存偏好',
      cancel: '取消',
      notificationTypes: {
        order_confirmed: '订单已确认',
        payment_pending: '待付款',
        payment_verified: '付款已验证',
        payment_failed: '付款失败',
        shipment_scheduled: '已安排发货',
        in_transit: '运输中',
        delivered: '已送达',
        dispute_opened: '争议已开启',
        dispute_resolved: '争议已解决',
        status_changed: '状态已更改',
        note_added: '已添加备注'
      },
      preferenceLabels: {
        orderConfirmedPush: '订单已确认（推送）',
        orderConfirmedEmail: '订单已确认（邮件）',
        paymentPendingPush: '待付款（推送）',
        paymentPendingEmail: '待付款（邮件）',
        paymentVerifiedPush: '付款已验证（推送）',
        paymentVerifiedEmail: '付款已验证（邮件）',
        paymentFailedPush: '付款失败（推送）',
        paymentFailedEmail: '付款失败（邮件）',
        shipmentScheduledPush: '已安排发货（推送）',
        shipmentScheduledEmail: '已安排发货（邮件）',
        inTransitPush: '运输中（推送）',
        inTransitEmail: '运输中（邮件）',
        deliveredPush: '已送达（推送）',
        deliveredEmail: '已送达（邮件）',
        disputeOpenedPush: '争议已开启（推送）',
        disputeOpenedEmail: '争议已开启（邮件）',
        disputeResolvedPush: '争议已解决（推送）',
        disputeResolvedEmail: '争议已解决（邮件）',
        statusChangedPush: '状态已更改（推送）',
        statusChangedEmail: '状态已更改（邮件）',
        noteAddedPush: '已添加备注（推送）',
        noteAddedEmail: '已添加备注（邮件）'
      }
    }
  };

  const t = content?.[language];

  useEffect(() => {
    loadData();
    checkBrowserPermission();

    // Subscribe to real-time notifications
    const channel = notificationService?.subscribeToNotifications((newNotification) => {
      setNotifications(prev => [newNotification, ...prev]);
      loadStats();
    });

    return () => {
      if (channel) {
        notificationService?.unsubscribe(channel);
      }
    };
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [activeView]);

  const checkBrowserPermission = () => {
    setBrowserPermission(notificationService?.isBrowserNotificationEnabled());
  };

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadNotifications(),
        loadPreferences(),
        loadStats()
      ]);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const loadNotifications = async () => {
    try {
      const filters = activeView === 'unread' ? { isRead: false } : {};
      const data = await notificationService?.getAll(filters);
      setNotifications(data);
    } catch (err) {
      setError(err?.message);
    }
  };

  const loadPreferences = async () => {
    try {
      const data = await notificationService?.getPreferences();
      setPreferences(data);
    } catch (err) {
      console.error('Failed to load preferences:', err);
    }
  };

  const loadStats = async () => {
    try {
      const data = await notificationService?.getStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationService?.markAsRead(notificationId);
      setNotifications(prev => 
        prev?.map(n => n?.id === notificationId ? { ...n, isRead: true, readAt: new Date()?.toISOString() } : n)
      );
      loadStats();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationService?.markAllAsRead();
      setNotifications(prev => 
        prev?.map(n => ({ ...n, isRead: true, readAt: new Date()?.toISOString() }))
      );
      loadStats();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await notificationService?.delete(notificationId);
      setNotifications(prev => prev?.filter(n => n?.id !== notificationId));
      loadStats();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleEnableBrowserNotifications = async () => {
    try {
      const granted = await notificationService?.requestBrowserPermission();
      setBrowserPermission(granted);
      if (granted) {
        setError('');
      } else {
        setError(language === 'en' ? 'Browser notification permission denied' : '浏览器通知权限被拒绝');
      }
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleSavePreferences = async () => {
    try {
      await notificationService?.updatePreferences(preferences);
      setShowPreferences(false);
      setError('');
    } catch (err) {
      setError(err?.message);
    }
  };

  const handlePreferenceChange = (key, value) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleString(language === 'en' ? 'en-US' : 'zh-CN');
  };

  const getNotificationIcon = (type) => {
    const iconMap = {
      order_confirmed: 'CheckCircle',
      payment_pending: 'Clock',
      payment_verified: 'DollarSign',
      payment_failed: 'XCircle',
      shipment_scheduled: 'Calendar',
      in_transit: 'Truck',
      delivered: 'Package',
      dispute_opened: 'AlertTriangle',
      dispute_resolved: 'CheckCircle2',
      status_changed: 'RefreshCw',
      note_added: 'FileText'
    };
    return iconMap?.[type] || 'Bell';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        <span className="ml-3 text-muted-foreground">{t?.loading}</span>
      </div>
    );
  }

  if (showPreferences && preferences) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-foreground">{t?.preferences}</h3>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowPreferences(false)}
            >
              {t?.cancel}
            </Button>
            <Button onClick={handleSavePreferences}>
              {t?.savePreferences}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.keys(t?.preferenceLabels)?.map((key) => (
            <label key={key} className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted transition-smooth">
              <input
                type="checkbox"
                checked={preferences?.[key] || false}
                onChange={(e) => handlePreferenceChange(key, e?.target?.checked)}
                className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
              />
              <span className="text-sm text-foreground">{t?.preferenceLabels?.[key]}</span>
            </label>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h3 className="text-xl font-semibold text-foreground">{t?.title}</h3>
        <div className="flex gap-2">
          {!browserPermission && (
            <Button
              variant="outline"
              onClick={handleEnableBrowserNotifications}
              className="flex items-center gap-2"
            >
              <Icon name="Bell" size={16} />
              {t?.enableBrowserNotifications}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => setShowPreferences(true)}
            className="flex items-center gap-2"
          >
            <Icon name="Settings" size={16} />
            {t?.preferences}
          </Button>
          {notifications?.some(n => !n?.isRead) && (
            <Button onClick={handleMarkAllRead}>
              {t?.markAllRead}
            </Button>
          )}
        </div>
      </div>

      {browserPermission && (
        <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <Icon name="CheckCircle" size={18} className="text-green-600 dark:text-green-400" />
          <span className="text-sm text-green-700 dark:text-green-300">{t?.browserNotificationsEnabled}</span>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <Icon name="AlertCircle" size={18} className="text-red-600 dark:text-red-400" />
          <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 bg-card border border-border rounded-lg">
            <div className="text-2xl font-bold text-foreground">{stats?.total}</div>
            <div className="text-sm text-muted-foreground">{t?.total}</div>
          </div>
          <div className="p-4 bg-card border border-border rounded-lg">
            <div className="text-2xl font-bold text-primary">{stats?.unread}</div>
            <div className="text-sm text-muted-foreground">{t?.unreadCount}</div>
          </div>
          <div className="p-4 bg-card border border-border rounded-lg">
            <div className="text-2xl font-bold text-green-600">{stats?.read}</div>
            <div className="text-sm text-muted-foreground">{t?.read}</div>
          </div>
          <div className="p-4 bg-card border border-border rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{stats?.byChannel?.both}</div>
            <div className="text-sm text-muted-foreground">{t?.both}</div>
          </div>
        </div>
      )}

      <div className="flex gap-2 border-b border-border">
        <button
          onClick={() => setActiveView('all')}
          className={`px-4 py-2 text-sm font-medium transition-smooth ${
            activeView === 'all' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
          }`}
        >
          {t?.all}
        </button>
        <button
          onClick={() => setActiveView('unread')}
          className={`px-4 py-2 text-sm font-medium transition-smooth ${
            activeView === 'unread' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
          }`}
        >
          {t?.unread}
        </button>
      </div>

      <div className="space-y-3">
        {notifications?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Bell" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">{t?.noNotifications}</p>
          </div>
        ) : (
          notifications?.map((notification) => (
            <div
              key={notification?.id}
              className={`p-4 border rounded-lg transition-smooth ${
                notification?.isRead
                  ? 'bg-card border-border' :'bg-primary/5 border-primary/20'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  notification?.isRead ? 'bg-muted' : 'bg-primary/10'
                }`}>
                  <Icon
                    name={getNotificationIcon(notification?.notificationType)}
                    size={20}
                    className={notification?.isRead ? 'text-muted-foreground' : 'text-primary'}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="font-semibold text-foreground">{notification?.title}</h4>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {!notification?.isRead && (
                        <button
                          onClick={() => handleMarkAsRead(notification?.id)}
                          className="text-xs text-primary hover:underline"
                        >
                          {t?.markRead}
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(notification?.id)}
                        className="text-xs text-red-600 hover:underline"
                      >
                        {t?.delete}
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{notification?.message}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                    <span>{t?.notificationTypes?.[notification?.notificationType]}</span>
                    <span>•</span>
                    <span>{formatDate(notification?.createdAt)}</span>
                    {notification?.channel && (
                      <>
                        <span>•</span>
                        <div className="flex items-center gap-1">
                          {(notification?.channel === 'push' || notification?.channel === 'both') && (
                            <Icon name="Smartphone" size={12} />
                          )}
                          {(notification?.channel === 'email' || notification?.channel === 'both') && (
                            <Icon name="Mail" size={12} />
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default NotificationsTab;